import '/assets/shared/sff-contract.js?v=20260822-statebridge4';


export function slugifyUrlSegment(str) {
  return window.SFFContracts?.fallbackSlug?.(str) || 'build';
}

export function caseRouteSlug(caseOrValue, fallbackName = '') {
  return window.SFFContracts?.caseRouteSlug?.(caseOrValue, fallbackName) || slugifyUrlSegment(
    caseOrValue && typeof caseOrValue === 'object' ? (caseOrValue.name || fallbackName) : (caseOrValue || fallbackName)
  );
}

export function normalizePresetName(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function formatCaseBrandTitle(rawInput) {
  if (!rawInput) return '';
  let formatted = String(rawInput).replace(/[-_]+/g, ' ');
  formatted = formatted.replace(/\b\w/g, char => char.toUpperCase());
  const replacements = [
    [/\bssupd\b/gi, 'SSUPD'], [/\bncase\b/gi, 'NCASE'], [/\bnzxt\b/gi, 'NZXT'], [/\bevga\b/gi, 'EVGA'],
    [/\basus\b/gi, 'ASUS'], [/\bmsi\b/gi, 'MSI'], [/\bformd\b/gi, 'FormD'], [/\blouqe\b/gi, 'LOUQE'],
    [/\bdan\b/gi, 'DAN'], [/\bv1\b/gi, 'V1'], [/\bv2\b/gi, 'V2'], [/\bv3\b/gi, 'V3'],
    [/\bv4\b/gi, 'V4'], [/\bv5\b/gi, 'V5'], [/\bsff\b/gi, 'SFF'], [/\bitx\b/gi, 'ITX'],
    [/\batx\b/gi, 'ATX'], [/\bgpu\b/gi, 'GPU'], [/\bcpu\b/gi, 'CPU'], [/\baio\b/gi, 'AIO']
  ];
  replacements.forEach(([regex, replacement]) => { formatted = formatted.replace(regex, replacement); });
  return formatted;
}

function replaceCleanWorkbenchRoute(caseRecordOrValue) {
  try {
    const path = window.SFFContracts?.workbenchPath?.(caseRecordOrValue)
      || `/3d/${caseRouteSlug(caseRecordOrValue)}/`;
    const next = new URL(window.location.href);
    next.pathname = path;
    // A user-selected case leaves any imported-build/query route behind. A literal fragment
    // remains useful for in-page anchors, but ?case= and the schema-2 path token must not
    // continue to describe the previous build. replaceState is deliberate because 3D does not
    // run a popstate scene transaction; pushing would create URL-only history entries whose
    // scene could not be restored on Back.
    next.search = '';
    const target = `${next.pathname}${next.hash}`;
    if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== target) {
      window.history.replaceState({ sff3dCaseSlug: caseRouteSlug(caseRecordOrValue) }, '', target);
    }
  } catch (error) {
    console.warn('[SFF 3D] Could not synchronize clean case route:', error);
  }
}

export function createCaseNavigation({
  getCases,
  getCaseBySlug,
  getCaseByNormalizedName,
  spawnCase
} = {}) {
  function resolvePresetCase(requestedName) {
    const requested = String(requestedName || '').trim();
    const cases=getCases?.() || [];
    if (!requested || !cases.length) return null;
    return getCaseBySlug?.(caseRouteSlug(requested))
      || getCaseByNormalizedName?.(normalizePresetName(requested))
      || null;
  }

  function addCaseToMat(caseName, { syncRoute = true } = {}) {
    const cases=getCases?.() || [];
    if (!cases.length) return;
    const querySlug = caseRouteSlug(caseName);
    const queryNorm = normalizePresetName(caseName);
    const matched = getCaseBySlug?.(querySlug)
      || getCaseByNormalizedName?.(queryNorm)
      || cases.find(row => row?.name?.toLowerCase().includes(String(caseName || '').toLowerCase()));
    if (matched) {
      spawnCase?.(formatCaseBrandTitle(matched.name), `${matched.l} x ${matched.w} x ${matched.h} mm`, matched);
      if (syncRoute) replaceCleanWorkbenchRoute(matched);
    } else {
      spawnCase?.(formatCaseBrandTitle(caseName), '300 x 150 x 200 mm', null);
      if (syncRoute) replaceCleanWorkbenchRoute(caseName);
    }
  }

  function checkUrlQueryParamsForCase() {
    try {
      const cases=getCases?.() || [];
      const urlParams = new URLSearchParams(window.location.search);
      let rawCaseParam = urlParams.get('case');
      if (!rawCaseParam) {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const index = pathSegments.indexOf('3d');
        if (index !== -1 && pathSegments.length > index + 1) rawCaseParam = pathSegments[index + 1];
      }
      if (!rawCaseParam) return;

      let decodedQuery = rawCaseParam;
      try { decodedQuery = decodeURIComponent(rawCaseParam); } catch (_) {}
      const querySlug = caseRouteSlug(decodedQuery);
      const queryTokens = decodedQuery.toLowerCase().split(/[\s\-_]+/).filter(Boolean);

      let matched = getCaseBySlug?.(querySlug) || null;
      if (!matched) {
        matched = cases.find(row => {
          const slug=caseRouteSlug(row);
          return slug.includes(querySlug) || querySlug.includes(slug);
        });
      }
      if (!matched && queryTokens.length) {
        matched = cases.find(row => {
          const name=String(row?.name || '').toLowerCase();
          return queryTokens.every(token => name.includes(token));
        });
      }

      if (matched) {
        spawnCase?.(formatCaseBrandTitle(matched.name), `${matched.l} x ${matched.w} x ${matched.h} mm`, matched);
        return;
      }

      const dimMatch = decodedQuery.match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/i);
      if (dimMatch) spawnCase?.(formatCaseBrandTitle(rawCaseParam), `${dimMatch[1]} x ${dimMatch[2]} x ${dimMatch[3]} mm`, null);
    } catch (error) {
      console.error('Error executing URL auto-spawn loop:', error);
    }
  }

  function bindPopularPresetButtons() {
    const grid=document.querySelector('.preset-grid');
    if (!grid) return;
    const legacyPresetMap = {
      't1 sandwich': 'FormD T1 Sandwich V2.1',
      'terra': 'Fractal Design Terra',
      'ridge': 'Fractal Design Ridge',
      'ncase m1': 'NCASE M2',
      'ncase m2': 'NCASE M2',
      'nfc s5m': 'NFC x NCASE S5M',
      'cm nr200': 'Cooler Master MasterBox NR200',
      'dan a4-sfx': 'DAN Cases A4-SFX v4.1',
      'meshroom s v2': 'SSUPD Meshroom S V2'
    };

    grid.querySelectorAll('button').forEach(button => {
      const legacyToken = button.textContent.replace('+','').trim().toLowerCase();
      if (!button.dataset.casePreset && legacyPresetMap[legacyToken]) button.dataset.casePreset = legacyPresetMap[legacyToken];
      if (button.dataset.casePreset === 'NCASE M2') button.textContent = '+ NCASE M2';
      if (button.dataset.casePreset === 'FormD T1 Sandwich V2.1') button.textContent = '+ T1 V2.1';
    });

    grid.addEventListener('click', event => {
      const button=event.target.closest('button');
      if (!button) return;
      const requestedName=button.dataset.casePreset || '';
      const matched=resolvePresetCase(requestedName);
      if (!matched) {
        console.warn(`[SFF 3D] Preset case not found exactly: ${requestedName}`);
        return;
      }
      addCaseToMat(matched.name);
    });
  }

  return { addCaseToMat, checkUrlQueryParamsForCase, resolvePresetCase, bindPopularPresetButtons };
}
